import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { fetchPortfolios, recalculateHoldings, type Portfolio } from '../lib/data'

const TRADE_TYPES = [
  { value: 'BUY', label: 'Buy' },
  { value: 'SELL', label: 'Sell' },
  { value: 'DIVIDEND', label: 'Dividend' },
  { value: 'DRIP', label: 'Dividend Reinvestment' },
  { value: 'FEE', label: 'Fee' },
  { value: 'INTEREST', label: 'Interest' },
  { value: 'TRANSFER', label: 'Transfer' },
]

function AddTrade() {
  const navigate = useNavigate()
  const [portfolios, setPortfolios] = useState<Portfolio[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const [portfolioId, setPortfolioId] = useState('')
  const [ticker, setTicker] = useState('')
  const [tradeType, setTradeType] = useState('BUY')
  const [tradeDate, setTradeDate] = useState(new Date().toISOString().split('T')[0])
  const [quantity, setQuantity] = useState('')
  const [pricePence, setPricePence] = useState('')
  const [valueGbp, setValueGbp] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    loadPortfolios()
  }, [])

  // Auto-calculate value from quantity × price
  useEffect(() => {
    const qty = parseFloat(quantity)
    const price = parseFloat(pricePence)
    if (!isNaN(qty) && !isNaN(price)) {
      const value = (qty * price) / 100
      setValueGbp(value.toFixed(2))
    }
  }, [quantity, pricePence])

  async function loadPortfolios() {
    try {
      const data = await fetchPortfolios()
      setPortfolios(data)
      if (data.length > 0) setPortfolioId(data[0].id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolios')
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!portfolioId || !ticker.trim()) return

    setLoading(true)
    setError(null)

    try {
      const qty = quantity ? parseFloat(quantity) : null
      const price = pricePence ? parseFloat(pricePence) : null
      const value = parseFloat(valueGbp) || 0

      // For sells and dividends, value should be negative (money out)
      const signedValue = (tradeType === 'SELL' || tradeType === 'DIVIDEND') ? -Math.abs(value) : Math.abs(value)

      // Generate a unique reference
      const reference = `MANUAL-${Date.now()}`

      const { error: insertError } = await supabase
        .from('transactions')
        .insert({
          portfolio_id: portfolioId,
          ticker: ticker.trim().toUpperCase(),
          type: tradeType,
          trade_date: tradeDate,
          settle_date: tradeDate,
          quantity: qty,
          price_pence: price,
          value_gbp: signedValue,
          reference,
          description: description.trim() || `${tradeType} ${ticker.trim().toUpperCase()}`,
        } as never)

      if (insertError) throw insertError

      // Recalculate holdings for this ticker
      await recalculateHoldings(portfolioId, ticker.trim().toUpperCase())

      setSuccess(true)
      setTicker('')
      setQuantity('')
      setPricePence('')
      setValueGbp('')
      setDescription('')

      // Navigate back after a short delay
      setTimeout(() => navigate(`/portfolio/${portfolioId}`), 1000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add trade')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <span className="text-4xl">📊</span>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Add Trade</h1>
          <p className="mt-2 text-gray-600">Manually add a transaction</p>
        </div>

        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
            ✅ Trade added successfully! Redirecting...
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
          {/* Portfolio selector */}
          <div>
            <label htmlFor="portfolio" className="block text-sm font-medium text-gray-700 mb-1">
              Portfolio
            </label>
            <select
              id="portfolio"
              value={portfolioId}
              onChange={(e) => setPortfolioId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            >
              {portfolios.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Ticker */}
          <div>
            <label htmlFor="ticker" className="block text-sm font-medium text-gray-700 mb-1">
              Ticker / Symbol
            </label>
            <input
              type="text"
              id="ticker"
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              placeholder="e.g. SHEL.L, NVDA"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          {/* Trade type */}
          <div>
            <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              id="type"
              value={tradeType}
              onChange={(e) => setTradeType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {TRADE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Date */}
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
              Trade Date
            </label>
            <input
              type="date"
              id="date"
              value={tradeDate}
              onChange={(e) => setTradeDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
          </div>

          {/* Quantity & Price row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
                Quantity
              </label>
              <input
                type="number"
                id="quantity"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="0"
                step="any"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
                Price (pence)
              </label>
              <input
                type="number"
                id="price"
                value={pricePence}
                onChange={(e) => setPricePence(e.target.value)}
                placeholder="0"
                step="any"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
          </div>

          {/* Total value */}
          <div>
            <label htmlFor="value" className="block text-sm font-medium text-gray-700 mb-1">
              Value (£)
            </label>
            <input
              type="number"
              id="value"
              value={valueGbp}
              onChange={(e) => setValueGbp(e.target.value)}
              placeholder="0.00"
              step="0.01"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Auto-calculated from quantity × price. Override if needed.
              {tradeType === 'SELL' && ' Sells use negative values automatically.'}
              {tradeType === 'DIVIDEND' && ' Dividends use negative values automatically.'}
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={`${tradeType} ${ticker || 'TICKER'}`}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 text-center">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !portfolioId || !ticker.trim()}
              className="flex-1 py-2 px-4 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Adding...' : 'Add Trade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AddTrade